export function restyleGeo({
  geoData,
  metricRows,
  group,
  props,
  setStyledGeo,
  getGroups,
  buildLabelsAndColors,
  makeHeatScale,
  make2pointHeatScale,
  onRankedUpdate,
  waveSelect,
}) {
  const {
    ActionMode,
    ActionTargetSubVariables,
    ActionTargetConcentration,
    preferenceFilter,
    theme_colours,
  } = props;

  if (!geoData) return setStyledGeo(null);
  if (!metricRows?.length || !geoData?.features?.length) return;
  if (
    !group ||
    (!group.keys?.length && ActionMode === "explore") ||
    (!group?.TargetVariable1?.keys?.length && ActionMode === "filter")
  ) return;

  // NZ: area_cd = String(area_id) set in loadGeo; rows carry area_id (int)
  const getRowId = (obj) => String(obj?.area_cd ?? obj?.area_id ?? "");

  const computeNet = (row, polarity) => {
    const toArr = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
    const sumArr = (keys) => keys.reduce((acc, k) => acc + (Number(row?.[k]) || 0), 0);
    const posKeys = toArr(polarity?.pos);
    const negKeys = toArr(polarity?.neg);
    const hasData = [...posKeys, ...negKeys].some((k) => Number.isFinite(Number(row?.[k])));
    if (!hasData) return null;
    return sumArr(posKeys) - sumArr(negKeys);
  };

  const getConcentrationSet = (byId, features, group, subVar, concentration) => {
    if (!features?.length) return null;
    if (!concentration || concentration === "all") return null;
    const pctMap = { top50: 50, top25: 25, top10: 10 };
    const pct = pctMap[concentration];
    if (!pct) return null;
    const toPct = (x) => (!Number.isFinite(x) ? null : x <= 1 ? x * 100 : x);
    const toArr = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
    const sumKeys = (keys, row) => keys.reduce((acc, k) => acc + (Number(row?.[k]) || 0), 0);
    const values = features
      .map((f) => {
        const id = getRowId(f?.properties);
        const row = byId?.get(id);
        if (!row) return null;
        let val;
        if (subVar === "net") {
          const pos = sumKeys(toArr(group?.TargetVariable1?.polarity?.pos), row);
          const neg = sumKeys(toArr(group?.TargetVariable1?.polarity?.neg), row);
          val = Number.isFinite(pos) && Number.isFinite(neg) ? pos - neg : null;
        } else {
          val = Number(row?.[subVar]);
        }
        const pctVal = toPct(val);
        return Number.isFinite(pctVal) ? { id, v: pctVal } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.v - a.v);
    if (!values.length) return null;
    const keepCount = Math.max(1, Math.ceil((pct / 100) * values.length));
    return new Set(values.slice(0, keepCount).map((d) => d.id));
  };

  const buildAllValues = (row, keys, labels) => {
    if (!row || typeof row !== "object") {
      return { _allValues: {}, _allValuesByKey: {}, _allValuesSorted: [], n_raked: null };
    }
    const allValues = {};
    const allValuesByKey = {};
    const allValuesSorted = [];
    keys.forEach((k) => {
      const v = Number(row?.[k]);
      if (Number.isFinite(v)) {
        const label = labels?.[k] || k;
        allValues[label] = v;
        allValuesByKey[k] = v;
        allValuesSorted.push({ key: k, label, value: v });
      }
    });
    allValuesSorted.sort((a, b) => b.value - a.value);
    return {
      _allValues: allValues,
      _allValuesByKey: allValuesByKey,
      _allValuesSorted: allValuesSorted,
      n_raked: row?.n_raked ?? null,
    };
  };

  const styleFeature = (f, fillColor, topLabel, topValue, allValues) => ({
    ...f,
    properties: { ...f.properties, fillColor, topLabel, topValue, ...allValues },
  });

  const emptyFeature = (f) => styleFeature(f, "#cccccc", "", 0, {
    _allValues: {}, _allValuesByKey: {}, _allValuesSorted: [], n_raked: null,
  });

  // Wave filter (NZ uses lowercase 'wave')
  const filteredRows = waveSelect != null
    ? metricRows.filter((r) => {
        const w = r.wave ?? r.Wave;
        return w == null || Number(w) === Number(waveSelect);
      })
    : metricRows;

  const byId = new Map(filteredRows.map((r) => [getRowId(r), r]));
  const { labels, colors } = buildLabelsAndColors(getGroups(), theme_colours);

  // =========================================================================
  // FILTER MODE
  // =========================================================================
  if (ActionMode === "filter") {
    const concentration = ActionTargetConcentration || "all";
    const keepSet = getConcentrationSet(
      byId, geoData?.features || [], group,
      ActionTargetSubVariables?.TargetSubVariable1, concentration
    );

    const toPct = (x) => (!Number.isFinite(x) ? null : x <= 1 ? x * 100 : x);
    const selectedTargetSubVar = ActionTargetSubVariables?.TargetSubVariable1;
    const [selectedSubVarId, selectedSubMetricRaw] = String(selectedTargetSubVar || "").split("|");
    const metricToken = String(selectedSubMetricRaw || "trust").toLowerCase();
    const toArr = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
    const metricMode = ["distrust", "discomfort", "neg", "negative"].includes(metricToken)
      ? "neg"
      : ["trust", "comfort", "pos", "positive"].includes(metricToken)
        ? "pos"
        : metricToken;
    const isLikertGridTarget = group?.TargetVariable1?.type === "likert_grid";
    const targetSubGroup = isLikertGridTarget ? getGroups()?.[selectedSubVarId] : null;
    const firstPos = toArr(targetSubGroup?.polarity?.pos)[0];
    const firstNeg = toArr(targetSubGroup?.polarity?.neg)[0];
    const posText = String(targetSubGroup?.labels?.[firstPos] || "").toLowerCase();
    const negText = String(targetSubGroup?.labels?.[firstNeg] || "").toLowerCase();
    const usesComfortLabels = `${posText} ${negText}`.includes("comfort");
    const posLabel = usesComfortLabels ? "Comfort" : "Trust";
    const negLabel = usesComfortLabels ? "Discomfort" : "Distrust";
    const sumArr = (keys, row) => (keys || []).reduce((acc, k) => acc + (Number(row?.[k]) || 0), 0);

    const resolveTargetValue = (row) => {
      if (isLikertGridTarget) {
        const posKeys = toArr(targetSubGroup?.polarity?.pos);
        const negKeys = toArr(targetSubGroup?.polarity?.neg);
        const trustVal = sumArr(posKeys, row);
        const distrustVal = sumArr(negKeys, row);
        if (metricMode === "pos") return trustVal;
        if (metricMode === "neg") return distrustVal;
        if (metricMode === "net") { row.net1 = trustVal - distrustVal; return row.net1; }
        const v = Number(row?.[selectedSubMetricRaw]);
        return Number.isFinite(v) ? v : null;
      }
      if (selectedTargetSubVar === "net") {
        const netVal = computeNet(row, group?.TargetVariable1?.polarity || {});
        row.net1 = netVal;
        return netVal;
      }
      return Number(row?.[selectedTargetSubVar]);
    };

    const valuesForScale = filteredRows.map((row) => {
      const val = resolveTargetValue(row);
      const p = toPct(val);
      return Number.isFinite(p) ? p : NaN;
    });
    geoData.features.forEach((f) => {
      const row = byId.get(getRowId(f.properties));
      if (!row) return;
      const val = resolveTargetValue(row);
      const p = toPct(val);
      const pass = Number.isFinite(p) && (keepSet == null || keepSet.has(getRowId(row)));
      row.ActionTargetKey = pass ? p : null;
    });

    const heatScale =
      (selectedTargetSubVar === "net" || (isLikertGridTarget && metricMode === "net"))
        ? make2pointHeatScale("#2E7D32", "#C62828", valuesForScale)
        : makeHeatScale(
            isLikertGridTarget
              ? (metricMode === "neg" ? "#C62828" : "#2E7D32")
              : (colors?.[selectedTargetSubVar] || "#2E7D32"),
            valuesForScale
          );

    const styled = {
      type: "FeatureCollection",
      features: geoData.features.map((f) => {
        const row = byId.get(getRowId(f.properties));
        if (!row) return emptyFeature(f);
        const groupKeys = [
          ...((isLikertGridTarget ? targetSubGroup?.keys : group.TargetVariable1?.keys) || []),
          "ActionTargetKey", "net1",
        ];
        const allValues = buildAllValues(
          row, groupKeys,
          (isLikertGridTarget ? targetSubGroup?.labels : group.TargetVariable1?.labels) || group.labels
        );
        const v = Number(row.ActionTargetKey);
        const fillColor = Number.isFinite(v) ? heatScale(v) : "#cccccc";
        const topLabel = isLikertGridTarget
          ? (metricMode === "neg" ? negLabel : metricMode === "pos" ? posLabel : "Net")
          : (selectedTargetSubVar === "net" ? "Net Score" : labels[selectedTargetSubVar] || selectedTargetSubVar);
        return styleFeature(f, fillColor, topLabel, Number.isFinite(v) ? v : 0, allValues);
      }),
    };
    setStyledGeo(styled);

    const idToName = new Map(
      (geoData?.features || []).map((f) => [getRowId(f.properties), f.properties?.area_name || getRowId(f.properties)])
    );
    const ranked = filteredRows
      .map((r) => {
        const id = getRowId(r);
        if (!id) return null;
        const v = Number(r.ActionTargetKey);
        const score = Number.isFinite(v) ? Math.max(0, v) : null;
        return score == null ? null : { id, name: idToName.get(id) || id, score, label: topLabelFor(r), color: "#2e7d32" };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score);

    function topLabelFor(r) {
      if (isLikertGridTarget) return metricMode === "neg" ? negLabel : metricMode === "pos" ? posLabel : "Net";
      return selectedTargetSubVar === "net" ? "Net Score" : labels[selectedTargetSubVar] || selectedTargetSubVar;
    }

    onRankedUpdate?.(ranked);
    return;
  }

  // =========================================================================
  // EXPLORE MODE
  // =========================================================================
  const idToName = new Map(
    (geoData?.features || []).map((f) => [getRowId(f.properties), f.properties?.area_name || getRowId(f.properties)])
  );

  // ── LIKERT GRID ──────────────────────────────────────────────────────────
  if (group?.type === "likert_grid") {
    const subVarIds = group.subVariables || [];
    const isValidSubVar = subVarIds.includes((preferenceFilter || "").split("|")[0]);
    const isCompareMode = !preferenceFilter || preferenceFilter === "compare" || !isValidSubVar;

    if (isCompareMode) {
      const subGroups = (group.subVariables || [])
        .map((id) => ({ id, g: getGroups()[id] }))
        .filter((x) => x.g?.polarity);

      const styled = {
        type: "FeatureCollection",
        features: geoData.features.map((f) => {
          const row = byId.get(getRowId(f.properties));
          if (!row) return emptyFeature(f);
          const sectorNets = subGroups
            .map(({ id, g }) => ({ key: id, label: group.subLabels?.[id] || id, value: computeNet(row, g.polarity) }))
            .filter((x) => Number.isFinite(x.value))
            .sort((a, b) => b.value - a.value);
          const top = sectorNets[0];
          if (!top) return emptyFeature(f);
          const allValues = {
            _allValues: {},
            _allValuesByKey: Object.fromEntries(sectorNets.map((s) => [s.key, s.value])),
            _allValuesSorted: sectorNets,
            n_raked: row.n_raked ?? null,
          };
          return styleFeature(f, group.subColors?.[top.key] || "#999", top.label, top.value, allValues);
        }),
      };
      setStyledGeo(styled);
      onRankedUpdate?.([]);
      return;
    }

    const [subVarId, subKey] = preferenceFilter.split("|");
    const subGroup = getGroups()[subVarId];
    if (!subGroup) return;
    const isNet = !subKey;

    const values = geoData.features.map((f) => {
      const row = byId.get(getRowId(f.properties));
      if (!row) return NaN;
      return isNet ? computeNet(row, subGroup.polarity) : Number(row[subKey]);
    });
    const heatScale = isNet
      ? make2pointHeatScale("#2E7D32", "#C62828", values)
      : makeHeatScale(colors[subKey] || "#6e6e6e", values);

    const styled = {
      type: "FeatureCollection",
      features: geoData.features.map((f) => {
        const row = byId.get(getRowId(f.properties));
        const val = row ? (isNet ? computeNet(row, subGroup.polarity) : Number(row[subKey])) : NaN;
        const allValues = buildAllValues(row, subGroup.keys, subGroup.labels);
        const topLabel = isNet ? (group.subLabels?.[subVarId] || subVarId) : (subGroup.labels?.[subKey] || subKey);
        return styleFeature(f, Number.isFinite(val) ? heatScale(val) : "#cccccc", topLabel, val, allValues);
      }),
    };
    setStyledGeo(styled);
    onRankedUpdate?.([]);
    return;
  }

  const isLikert = group?.type === "likert";

  // ── CATEGORICAL ──────────────────────────────────────────────────────────
  if (!isLikert) {
    const pref = preferenceFilter?.toLowerCase();
    let rankIndex = 0;
    if (["second", "2nd"].includes(pref)) rankIndex = 1;
    else if (["third", "3rd"].includes(pref)) rankIndex = 2;

    const selectedKey = preferenceFilter && group.keys.includes(preferenceFilter) ? preferenceFilter : null;

    if (!selectedKey) {
      const styled = {
        type: "FeatureCollection",
        features: geoData.features.map((f) => {
          const row = byId.get(getRowId(f.properties));
          if (!row) return emptyFeature(f);
          const sorted = group.keys
            .map((k) => ({ k, v: Number(row?.[k]) }))
            .filter((x) => Number.isFinite(x.v))
            .sort((a, b) => b.v - a.v);
          const pick = sorted[rankIndex] || sorted[0];
          if (!pick) return emptyFeature(f);
          const allValues = buildAllValues(row, group.keys, group.labels);
          return styleFeature(f, colors[pick.k] || "#999", group.labels?.[pick.k] || pick.k, pick.v, allValues);
        }),
      };
      setStyledGeo(styled);

      const ranked = filteredRows
        .map((r) => {
          const id = getRowId(r);
          const sorted = group.keys
            .map((k) => ({ k, v: Number(r[k]) }))
            .filter((x) => Number.isFinite(x.v))
            .sort((a, b) => b.v - a.v);
          const pick = sorted[rankIndex] || sorted[0];
          if (!pick) return null;
          return { id, name: idToName.get(id) || id, score: pick.v, label: labels[pick.k] || pick.k, color: colors[pick.k] || "#999" };
        })
        .filter(Boolean)
        .sort((a, b) => b.score - a.score);
      onRankedUpdate?.(ranked);
      return;
    }

    const values = geoData.features.map((f) => {
      const row = byId.get(getRowId(f.properties));
      return row ? Number(row[selectedKey]) : NaN;
    });
    const heatScale = makeHeatScale(colors[selectedKey] || "#6e6e6e", values);
    const styled = {
      type: "FeatureCollection",
      features: geoData.features.map((f) => {
        const row = byId.get(getRowId(f.properties));
        const val = row ? Number(row[selectedKey]) : NaN;
        const allValues = buildAllValues(row, group.keys, group.labels);
        return styleFeature(f, Number.isFinite(val) ? heatScale(val) : "#cccccc", group.labels?.[selectedKey] || selectedKey, val, allValues);
      }),
    };
    setStyledGeo(styled);

    const ranked = filteredRows
      .map((r) => {
        const id = getRowId(r);
        const val = Number(r[selectedKey]);
        if (!Number.isFinite(val)) return null;
        return { id, name: idToName.get(id) || id, score: val, label: labels[selectedKey] || selectedKey, color: colors[selectedKey] || "#999" };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score);
    onRankedUpdate?.(ranked);
    return;
  }

  // ── LIKERT ───────────────────────────────────────────────────────────────
  if (preferenceFilter === "top") {
    const toArr = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
    const dkKeys = new Set(toArr(group?.polarity?.dk));
    const candidateKeys = (group.keys || []).filter((k) => !dkKeys.has(k));

    const styled = {
      type: "FeatureCollection",
      features: geoData.features.map((f) => {
        const row = byId.get(getRowId(f.properties));
        if (!row) return emptyFeature(f);
        const ranked = candidateKeys
          .map((k) => ({ k, v: Number(row?.[k]) }))
          .filter((x) => Number.isFinite(x.v))
          .sort((a, b) => b.v - a.v);
        const top = ranked[0];
        if (!top) return styleFeature(f, "#cccccc", "", 0, buildAllValues(row, group.keys, group.labels));
        return styleFeature(f, colors[top.k] || "#999", group.labels?.[top.k] || top.k, top.v, buildAllValues(row, group.keys, group.labels));
      }),
    };
    setStyledGeo(styled);

    const ranked = filteredRows
      .map((r) => {
        const id = getRowId(r);
        const top = candidateKeys
          .map((k) => ({ k, v: Number(r[k]) }))
          .filter((x) => Number.isFinite(x.v))
          .sort((a, b) => b.v - a.v)[0];
        if (!top) return null;
        return { id, name: idToName.get(id) || id, score: top.v, label: labels[top.k] || top.k, color: colors[top.k] || "#999" };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score);
    onRankedUpdate?.(ranked);
    return;
  }

  const heatKey = preferenceFilter === "net" && group.polarity
    ? "net"
    : group.keys.includes(preferenceFilter)
      ? preferenceFilter
      : group.keys[0];

  const values = geoData.features.map((f) => {
    const row = byId.get(getRowId(f.properties));
    if (!row) return NaN;
    return heatKey === "net" ? computeNet(row, group.polarity) : Number(row[heatKey]);
  });
  const heatScale = heatKey === "net"
    ? make2pointHeatScale("#2E7D32", "#C62828", values)
    : makeHeatScale(colors[heatKey] || "#6e6e6e", values);

  const styled = {
    type: "FeatureCollection",
    features: geoData.features.map((f) => {
      const row = byId.get(getRowId(f.properties));
      const val = row ? (heatKey === "net" ? computeNet(row, group.polarity) : Number(row[heatKey])) : NaN;
      const allValues = buildAllValues(row, group.keys, group.labels);
      const topLabel = heatKey === "net" ? "Net Score" : (group.labels?.[heatKey] || heatKey);
      return styleFeature(f, Number.isFinite(val) ? heatScale(val) : "#cccccc", topLabel, val, allValues);
    }),
  };
  setStyledGeo(styled);

  const ranked = filteredRows
    .map((r) => {
      const id = getRowId(r);
      const val = heatKey === "net" ? computeNet(r, group.polarity) : Number(r[heatKey]);
      if (!Number.isFinite(val)) return null;
      return {
        id, name: idToName.get(id) || id, score: val,
        label: heatKey === "net" ? "Net Score" : (labels[heatKey] || heatKey),
        color: heatKey === "net" ? (val >= 0 ? "#2e7d32" : "#c62828") : (colors[heatKey] || "#999"),
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score);
  onRankedUpdate?.(ranked);
}
